import type { ChangeEventHandler } from "react";
import {
  useFormContext,
  type RegisterOptions,
  useController,
} from "react-hook-form";
import { FormControlLabel, Radio, RadioGroup, Typography, FormControl } from "@mui/material";
import { useTranslations } from "next-intl";
import { getValidationRules } from "./utils/getValidationRules";
import { HelperMessage } from "./HelperMessage";
import { CheckRadioIcon, RadioIcon } from "./RadioIcon";

interface FormRadioButtonGroupProps {
  className?: string;
  labelClassName?: string;
  name: string;
  label?: string;
  required?: boolean;
  rules?: RegisterOptions;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  row?: boolean;
  options?: RadioOption[];
  showError?: boolean;
  fontStyle?: string;
  isYesNo?: boolean;
  isBoolean?: boolean;
  disabled?: boolean;
}

export interface RadioOption {
  label: string;
  value: string | boolean;
}

export const FormRadioButtonGroup: React.FC<FormRadioButtonGroupProps> = ({
  className,
  labelClassName,
  name,
  required = true,
  rules = {},
  row = true,
  options,
  showError = true,
  isYesNo = false,
  onChange,
  isBoolean = false,
  disabled = false,
}) => {
  const t = useTranslations();

  if (!options && !isYesNo) {
    throw new Error(
      "Options are required for FormRadioButtonGroup unless isYesNo is true"
    );
  }

  const radioOptions = isYesNo
    ? [
        { label: t("common.yes"), value: true },
        { label: t("common.no"), value: false },
      ]
    : options;

  const validationRules =
    (isYesNo || isBoolean) && required
      ? {
          validate: (value: string | boolean | undefined) => {
            return value !== undefined || t("forms.validation.required");
          },
        }
      : getValidationRules({
          required,
          rules,
          t,
        });

  const {
    control,
    formState: { errors },
    setValue,
  } = useFormContext();

  const {
    field: { value: fieldValue },
  } = useController<Record<string, string | boolean | undefined>>({
    name,
    rules: validationRules,
    control,
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value =
      isBoolean || isYesNo
        ? Boolean(event.target.value === "true")
        : event.target.value;

    setValue(name, value, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <FormControl className={className ?? "body-l flex flex-1 flex-col"}>
      <RadioGroup
        data-cy={name}
        name={name}
        onChange={onChange ?? handleChange}
        row={row}
        sx={{ gap: "8px" }}
        value={fieldValue === undefined ? "" : fieldValue}
      >
        {radioOptions?.map(({ label, value }) => {
          return (
            <FormControlLabel
              className={labelClassName ?? "items-start"}
              control={
                <Radio
                  checked={fieldValue === value}
                  checkedIcon={<CheckRadioIcon disabled={disabled} />}
                  disabled={disabled}
                  icon={<RadioIcon />}
                  value={value}
                />
              }
              data-cy={label}
              key={label}
              label={
                <Typography
                  color="text.primary"
                  sx={{
                    lineHeight: "38px",
                  }}
                >
                  {label}
                </Typography>
              }
            />
          );
        })}
      </RadioGroup>
      <HelperMessage errors={errors} name={name} showError={showError} />
    </FormControl>
  );
};
